import { Button, Header, Input } from '@ws/shared/components';
import { TEntityRef } from '@ws/shared/types';
import { Close24, Sync20, SyncSlash20 } from '@ws/shared/icons';
import { useCallback, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EditabilityContext } from '../../../../context/EditabilityContext';
import { TAppState } from '../../../../redux';
import { setSelectedSecondaryNode } from '../../../../redux/editor';
import { setGlobalIsSecondaryCompiled } from '../../../../redux/general/general.actions';
import { useDebouncedNameSave } from '../../../../hooks/useDebouncedNameSave';
import { transliterate } from '../../../../utils/transliterate';

export function HeaderContainer() {
  const dispatch = useDispatch();
  const { isSecondaryCompiled } = useSelector((state: TAppState) => state.general);
  const { getNodeByRef, updateNodeMeta, updateProjectMeta } = useContext(EditabilityContext);

  const { secondarySelected, project } = useSelector((state: TAppState) => state.editor);

  const currentNode = useMemo(
    () => (secondarySelected ? getNodeByRef(secondarySelected) : secondarySelected),
    [getNodeByRef, secondarySelected],
  );

  const isCollection = currentNode?.type === 'collection';

  const handleCompilationClick = useCallback(
    () => dispatch(setGlobalIsSecondaryCompiled({ isSecondaryCompiled: !isSecondaryCompiled })),
    [dispatch, isSecondaryCompiled],
  );

  const handleNameSave = useCallback(
    (nameToSave: string, owner: TEntityRef) => {
      updateNodeMeta(owner, { name: nameToSave });

      if (owner.id === project?.root) {
        updateProjectMeta({ name: nameToSave, slug: transliterate(nameToSave) });
      }
    },
    [updateNodeMeta, updateProjectMeta, project?.root],
  );

  const {
    name,
    onChange: handleNameChange,
    onBlur: handleNameInputBlur,
  } = useDebouncedNameSave({
    owner: secondarySelected,
    currentName: currentNode?.meta.name || '',
    onSave: handleNameSave,
  });

  function handleSecondaryBoardClose() {
    dispatch(setSelectedSecondaryNode({ secondarySelected: null }));
  }

  if (!secondarySelected || !currentNode) {
    return null;
  }

  return (
    <Header
      title={
        <Input
          variant="shapeless"
          value={name}
          onChange={handleNameChange}
          onBlur={handleNameInputBlur}
        />
      }
      after={
        isCollection && (
          <Button
            style={{ display: 'inline-flex' }}
            key="compile_btn"
            color={isSecondaryCompiled ? 'secondary' : 'primary'}
            onClick={handleCompilationClick}
            icon={isSecondaryCompiled ? <SyncSlash20 /> : <Sync20 />}
          />
        )
      }
      ending={
        <Button
          style={{ display: 'inline-flex' }}
          key="close_btn"
          variant="shapeless"
          onClick={handleSecondaryBoardClose}
          icon={<Close24 />}
        />
      }
    />
  );
}
